import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getGscPanelData, type GscPanelData, type GscUrlRow } from "@/lib/gsc-panel.functions";
import { submitSitemapToGSC } from "@/lib/gsc.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/gsc")({
  component: AdminGsc,
  ssr: false,
  head: () => ({
    meta: [
      { title: "GSC — Stato indicizzazione" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function fmt(n: number, digits = 0) {
  return n.toLocaleString("it-CH", { maximumFractionDigits: digits });
}
function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-CH", { dateStyle: "short", timeStyle: "short" });
}
function shortPath(url: string) {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch {
    return url;
  }
}

function Sparkline({ daily, days }: { daily: GscUrlRow["daily"]; days: number }) {
  const W = 140, H = 32, PAD = 2;
  if (!daily.length) {
    return <span style={{ color: "#999", fontSize: 11 }}>nessun dato</span>;
  }
  const max = Math.max(1, ...daily.map((d) => d.impressions));
  // Build a complete date axis to keep gaps visible
  const end = daily[daily.length - 1].date;
  const endDate = new Date(end + "T00:00:00Z");
  const points: Array<{ x: number; y: number }> = [];
  const byDate = new Map(daily.map((d) => [d.date, d.impressions]));
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(endDate);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    const v = byDate.get(key) ?? 0;
    const x = PAD + ((days - 1 - i) / (days - 1)) * (W - PAD * 2);
    const y = H - PAD - (v / max) * (H - PAD * 2);
    points.push({ x, y });
  }
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} aria-label={`Impressioni ultimi ${days} giorni, max ${max}`}>
      <path d={path} fill="none" stroke="#2c6e8c" strokeWidth={1.5} />
      {points.map((p, i) => (p.y < H - PAD ? <circle key={i} cx={p.x} cy={p.y} r={1.2} fill="#2c6e8c" /> : null))}
    </svg>
  );
}

function AdminGsc() {
  const { user, loading: authLoading, isAdmin, rolesLoading } = useAuth();
  const [days, setDays] = useState(28);
  const [data, setData] = useState<GscPanelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitMsg, setResubmitMsg] = useState<string | null>(null);
  const fetchData = useServerFn(getGscPanelData);
  const resubmitSitemap = useServerFn(submitSitemapToGSC);

  const load = async (d = days) => {
    setLoading(true); setErr(null);
    try {
      const r = await fetchData({ data: { days: d } });
      setData(r);
    } catch (e) {
      setErr((e as Error).message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data.urls].sort((a, b) => b.totals.impressions - a.totals.impressions);
  }, [data]);

  const counts = useMemo(() => {
    if (!data) return { total: 0, indexed: 0, missing: 0 };
    const total = data.urls.length;
    const indexed = data.urls.filter((u) => u.indexed).length;
    const missing = data.urls.filter((u) => u.inSitemap && !u.indexed).length;
    return { total, indexed, missing };
  }, [data]);

  if (authLoading || rolesLoading) {
    return <div style={{ padding: 40, fontFamily: "system-ui" }}>Caricamento…</div>;
  }
  if (!user) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: 24, fontFamily: "system-ui" }}>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>Accesso richiesto</h1>
        <p style={{ color: "#666" }}>
          Effettua il login dalla pagina <a href="/admin/news" style={{ color: "#2c6e8c" }}>/admin/news</a> per accedere al pannello GSC.
        </p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: 24, fontFamily: "system-ui" }}>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>Accesso negato</h1>
        <p style={{ color: "#666" }}>Serve il ruolo <strong>admin</strong> per visualizzare questo pannello.</p>
        <button onClick={() => supabase.auth.signOut()} style={btnGhost}>Esci</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "32px auto", padding: 24, fontFamily: "system-ui" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Google Search Console — Sitemap & Indicizzazione</h1>
          <p style={{ color: "#666", margin: "4px 0 0", fontSize: 13 }}>
            Stato di ogni URL della sitemap negli ultimi {days} giorni (impressioni = pagina indicizzata).
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#666" }}>
            Periodo:
            <select value={days} onChange={(e) => { const d = Number(e.target.value); setDays(d); void load(d); }}
              style={{ marginLeft: 6, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6 }}>
              <option value={7}>7 giorni</option>
              <option value={28}>28 giorni</option>
              <option value={90}>90 giorni</option>
            </select>
          </label>
          <button onClick={() => load()} disabled={loading} style={btnPrimary}>
            {loading ? "…" : "Aggiorna"}
          </button>
        </div>
      </div>

      {err && <div style={{ padding: 12, background: "#fde8e8", color: "#9b1c1c", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>Errore: {err}</div>}

      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
            <Card label="URL sitemap" value={String(counts.total)} />
            <Card label="Con impressioni" value={`${counts.indexed} / ${counts.total}`} tone={counts.indexed > 0 ? "ok" : "warn"} />
            <Card label="In attesa di indicizzazione" value={String(counts.missing)} tone={counts.missing > 0 ? "warn" : "ok"} />
            <Card label="Errori sitemap" value={String(data.sitemap?.errors ?? 0)} tone={(data.sitemap?.errors ?? 0) > 0 ? "err" : "ok"} />
            <Card label="Avvisi sitemap" value={String(data.sitemap?.warnings ?? 0)} tone={(data.sitemap?.warnings ?? 0) > 0 ? "warn" : "ok"} />
          </div>

          <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <strong>Sitemap:</strong> {data.sitemap?.path ?? "—"}<br />
                <span style={{ color: "#555" }}>
                  Ultimo invio: {fmtDate(data.sitemap?.lastSubmitted ?? null)} ·
                  Ultimo download Google: {fmtDate(data.sitemap?.lastDownloaded ?? null)} ·
                  Inviati: {data.sitemap?.submitted ?? 0} · Indicizzati (Google): {data.sitemap?.indexed ?? 0}
                  {data.sitemap?.isPending && <> · <em>in attesa</em></>}
                </span>
              </div>
              <button
                disabled={resubmitting}
                onClick={async () => {
                  setResubmitting(true); setResubmitMsg(null);
                  try {
                    const r = await resubmitSitemap({ data: { sitemapPath: "sitemap.xml" } });
                    setResubmitMsg(r.ok ? "Sitemap re-inviata a Google." : `Errore (${r.status}): ${r.body || "—"}`);
                    await load();
                  } catch (e) {
                    setResubmitMsg("Errore: " + (e as Error).message);
                  } finally {
                    setResubmitting(false);
                  }
                }}
                style={btnGhost}
              >
                {resubmitting ? "Invio…" : "Re-invia sitemap"}
              </button>
            </div>
            {resubmitMsg && <div style={{ marginTop: 8, color: "#444" }}>{resubmitMsg}</div>}
          </div>

          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ background: "#f1f5f9" }}>
                <tr>
                  <Th>URL</Th>
                  <Th>Stato</Th>
                  <Th align="right">Impressioni</Th>
                  <Th align="right">Click</Th>
                  <Th align="right">Pos. media</Th>
                  <Th>Andamento ({days}g)</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((u) => (
                  <tr key={u.url} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <Td>
                      <a href={u.url} target="_blank" rel="noreferrer" style={{ color: "#2c6e8c", textDecoration: "none" }}>
                        {shortPath(u.url)}
                      </a>
                      {!u.inSitemap && (
                        <span title="URL non presente nella sitemap" style={{ marginLeft: 6, fontSize: 10, color: "#92400e", background: "#fef3c7", padding: "1px 6px", borderRadius: 4 }}>fuori sitemap</span>
                      )}
                    </Td>
                    <Td>
                      {u.indexed ? (
                        <Badge tone="ok">indicizzata</Badge>
                      ) : (
                        <Badge tone="warn">in attesa</Badge>
                      )}
                    </Td>
                    <Td align="right">{fmt(u.totals.impressions)}</Td>
                    <Td align="right">{fmt(u.totals.clicks)}</Td>
                    <Td align="right">{u.totals.impressions > 0 ? fmt(u.totals.position, 1) : "—"}</Td>
                    <Td>
                      <Sparkline daily={u.daily} days={days} />
                    </Td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><Td colSpan={6} align="center" muted>Nessun URL trovato.</Td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p style={{ color: "#666", fontSize: 12, marginTop: 12 }}>
            Dati GSC aggiornati il {fmtDate(data.fetchedAt)} · finestra {data.range.startDate} → {data.range.endDate} ·
            Nota: Google ha un ritardo di circa 2 giorni nei dati di Search Analytics.
          </p>
        </>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: "8px 14px", background: "#2c6e8c", color: "#fff", border: 0, borderRadius: 6, fontSize: 13, cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  padding: "8px 14px", background: "#fff", color: "#2c6e8c", border: "1px solid #2c6e8c", borderRadius: 6, fontSize: 13, cursor: "pointer",
};

function Card({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "err" }) {
  const bg = tone === "ok" ? "#ecfdf5" : tone === "warn" ? "#fffbeb" : tone === "err" ? "#fef2f2" : "#f8fafc";
  const fg = tone === "ok" ? "#065f46" : tone === "warn" ? "#92400e" : tone === "err" ? "#9b1c1c" : "#0f172a";
  return (
    <div style={{ background: bg, border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: fg, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: "ok" | "warn" | "err" }) {
  const bg = tone === "ok" ? "#dcfce7" : tone === "warn" ? "#fef3c7" : "#fee2e2";
  const fg = tone === "ok" ? "#166534" : tone === "warn" ? "#92400e" : "#9b1c1c";
  return <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{children}</span>;
}

function Th({ children, align }: { children: React.ReactNode; align?: "right" | "left" }) {
  return <th style={{ textAlign: align ?? "left", padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>{children}</th>;
}
function Td({ children, align, muted, colSpan }: { children: React.ReactNode; align?: "right" | "left" | "center"; muted?: boolean; colSpan?: number }) {
  return <td colSpan={colSpan} style={{ textAlign: align ?? "left", padding: "10px 12px", color: muted ? "#94a3b8" : "#0f172a", verticalAlign: "middle" }}>{children}</td>;
}
